import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { ReactElement, useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { useTranslation } from "react-i18next";
import { getDefaultSubgradeColumnsConfig, getStaticColumnsConfig, registerCustomFilters } from "./FilterTemplates";
import {errorToast, vesselBodyTemplate} from "@idwal/idwal-react-components";
import {subGradeActionBodyTemplate} from "../Shared/BodyTemplates";
import { SharedTableColumnConfig } from "../../types/SharedTableColumnConfig";
import LOCAL_TABLE_CONFIG_KEY from "../../types/TableLocalConfigKeys";
import { onCustomRestoreState } from "../../utils/TableUtils";
import { VesselSubgradeDataItem } from "../../types/VesselSubgradeDataItem";
import { applyGlobalFilter } from "../Shared/GlobalFilter";
import {
    buildColumns,
    exportTable,
    loadSavedColumnConfigurationsV2,
    onColumnReorder,
    onColumnToggle,
    updateStoredTableConfigState,
} from "../Shared/TableFunctions";
import { DefaultHeader } from "../Shared/DefaultHeader";
import { SubgradeTableFields } from "./SubgradeTableFields";
import { MultiSelectChangeEvent } from "primereact/multiselect";
import { columnTemplates, filterTemplates, keywordSearchFilterFields } from "./Constants";
import { ANALYTICS_TAB_CLICK, SUB_GRADE_OVERVIEW_EXPORT_CLICK } from "../../constants/analytics";
import { sendGAEvent } from "../../AnalyticsService";
import { HeaderType } from "../../types/HeaderType";
import {actionColumn, vesselColumn} from "../FleetTable/Columns/StaticColumns";
import { USER_APP_STATE_SUBGRADE_TABLE_KEY } from "../../utils/Constants";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import { selectDataTableFilterData, selectGlobalFilters, selectKeywordSearch } from "../../store/selectors/filtersSelectors";
import { selectUserDetails } from "../../store/selectors/userSelectors";
import { setSubGradeTableFilterData } from "../../store/slices/filtersSlice";
import { selectVesselByImo } from "../../store/thunks/vesselViewThunks";
import { selectFleetGraph } from "../../store/selectors/fleetGraphSelectors";

export const SubgradeTable = (): ReactElement => {
    const { t } = useTranslation("locale");
    const dispatch = useAppDispatch();
    const globalFilters = useAppSelector(selectGlobalFilters);
    const userDetails = useAppSelector(selectUserDetails);
    const keywordSearch = useAppSelector(selectKeywordSearch);
    const { subGrade: filters } = useAppSelector(selectDataTableFilterData);

    const [shipTypes, setShipTypes] = useState<string[]>([]);
    const [technicalManagers, setTechnicalManagers] = useState<string[]>([]);
    const [filteredData, setFilteredData] = useState<VesselSubgradeDataItem[]>([]);
    const [columns, setColumns] = useState<SharedTableColumnConfig[]>(getDefaultSubgradeColumnsConfig());
    const [selectedColumns, setSelectedColumns] = useState(columns);
    const subgradeTableRef = useRef<any>(null);

    const {
        subgradeTableData,
        subgradeError,
        subgradeLoading,
    } = useAppSelector(selectFleetGraph);

    registerCustomFilters();

    useEffect(() => {
        if (!subgradeTableData.length) {
            return;
        }

        loadSavedColumnConfigurationsV2(
            columns, setColumns, setSelectedColumns, userDetails, userDetails?.appState?.subgradeTable, USER_APP_STATE_SUBGRADE_TABLE_KEY, LOCAL_TABLE_CONFIG_KEY.SUBGRADE_COLUMNS);

        let data = subgradeTableData.length ? subgradeTableData.filter(applyFilter) : subgradeTableData;

        setFilteredData(data);

        const distinctShipTypes = Array.from(new Set([...data.flatMap((e: VesselSubgradeDataItem) => e.type)]));
        setShipTypes(distinctShipTypes);

        const distinctTechnicalManagers = Array.from(
            new Set([...data.flatMap((e: VesselSubgradeDataItem) => e.technicalManager)]),
        )
            .filter((e) => !!e)
            .sort((a, b) => a.localeCompare(b));
        setTechnicalManagers(distinctTechnicalManagers);
        }, [subgradeTableData, globalFilters]);

    if (subgradeError) {
        errorToast(t("subgrades.tableLoadErrorMessage"));
        return <div />;
    }

    const applyFilter = (dataItem: VesselSubgradeDataItem) => {
        return (
            applyGlobalFilter(globalFilters, dataItem.vesselName, dataItem.type, dataItem.technicalManager, dataItem.shipAge)
        );
    };

    const onFilter = (event: DataTableStateEvent) => {
        dispatch(setSubGradeTableFilterData(event.filters));
    }

    const selectVessel = (rowData: any) => {
        dispatch(selectVesselByImo(rowData.vessel.imo));
    }

    const header = () => (
        <DefaultHeader
            onColumnToggle={(e: MultiSelectChangeEvent) =>
                onColumnToggle(e, LOCAL_TABLE_CONFIG_KEY.SUBGRADE_COLUMNS, columns, setSelectedColumns, userDetails, USER_APP_STATE_SUBGRADE_TABLE_KEY)
            }
            columns={columns}
            disabled={subgradeLoading}
            selectedColumns={selectedColumns}
            filters={filters}
            headerType={HeaderType.SUB_GRADE}
            onColumnReorder={(orderedColumns: any) =>
                onColumnReorder(
                    orderedColumns,
                    LOCAL_TABLE_CONFIG_KEY.SUBGRADE_COLUMNS,
                    LOCAL_TABLE_CONFIG_KEY.SUBGRADE_ORDERED_COLUMNS,
                    columns,
                    selectedColumns,
                    setColumns,
                    setSelectedColumns,
                    userDetails,
                    USER_APP_STATE_SUBGRADE_TABLE_KEY
                )
            }
        />
    );

    const onExport = () => {
        exportTable(getStaticColumnsConfig().concat(columns), filteredData, "Sub_Grade_Overview_", "csv");
        sendGAEvent(ANALYTICS_TAB_CLICK, SUB_GRADE_OVERVIEW_EXPORT_CLICK);
    };

    const columnComponents = buildColumns(
        selectedColumns,
        columnTemplates(),
        filterTemplates(shipTypes, technicalManagers),
        (e: SharedTableColumnConfig) =>
            e.field.includes("subgrades.") || e.field == SubgradeTableFields.IDWAL_GRADE ? "12rem" : "14rem",
    );

    const customSaveState = (state: any) => {
        updateStoredTableConfigState(state, LOCAL_TABLE_CONFIG_KEY.SUBGRADE);
    }

    const customRestoreState = () => {
        return onCustomRestoreState(
            LOCAL_TABLE_CONFIG_KEY.SUBGRADE, keywordSearch.subGrade);
    }

    return (
        <>
            <div className={"table-container subgrade-table-container m-0 p-0"} data-cy="subgrade-table-container">
                <DataTable
                    ref={subgradeTableRef}
                    globalFilterFields={keywordSearchFilterFields}
                    scrollable
                    data-cy="subgrade-table"
                    filters={filters}
                    filterDisplay="row"
                    header={header()}
                    value={filteredData}
                    responsiveLayout="scroll"
                    onFilter={onFilter}
                    stateStorage="custom"
                    customSaveState={(state) => customSaveState(state)}
                    customRestoreState={customRestoreState}
                    loading={subgradeLoading}
                    emptyMessage={t("subgrades.noDataMessage")}
                    removableSort
                    stripedRows
                    resizableColumns
                    columnResizeMode="expand"
                >
                    {vesselColumn((e: any) => vesselBodyTemplate({ vessel: e }, selectVessel))}
                    {actionColumn( (e: any) => subGradeActionBodyTemplate(e))}
                    {columnComponents}
                </DataTable>
            </div>
            <div className="flex justify-content-center mb-3 mt-3">
                <Button
                    icon="pi pi-file-export"
                    iconPos="left"
                    disabled={subgradeLoading || !subgradeTableData.length || !filteredData.length}
                    onClick={onExport}
                    label={t("fleetTable.export")}
                    data-cy="export-button"
                />
            </div>
        </>
    );
};
